<script lang="ts" setup>
import type { FieldDefinition } from "~/models/Automation";
import MediaLibrary from "~/components/MediaLibrary.vue";
import { faImage } from "@fortawesome/pro-light-svg-icons";
import type { Media } from "~/stores/media-library";

const field = defineModel<FieldDefinition>({
    required: true,
    default: () => ({
        key: "",
        type: "string",
        sub_type: null,
        label: "",
        default_value: "",
    }),
});

const props = withDefaults(
    defineProps<{
        fields: FieldDefinition[];
        label: string;
        name: string;
    }>(),
    {
        fields: () => [],
        name: "default_value",
        label: "Default value",
    },
);

const dates = computed(() =>
    props.fields.filter((field) =>
        ["datetime", "time", "date"].includes(field.type),
    ),
);

const phones = computed(() =>
    props.fields.filter((field) =>
        ["phone"].includes(field.sub_type as string),
    ),
);

const emails = computed(() =>
    props.fields.filter((field) =>
        ["email"].includes(field.sub_type as string),
    ),
);

function chooseImage(media: Media, toggle: () => void) {
    field.value = { ...field.value, default_value: media.full_url };

    toggle();
}
</script>

<template>
    <UFormGroup :label="label" :name="name">
        <template #hint>
            <UButton
                color="white"
                size="xs"
                @click="field = { ...field, default_value: null }"
            >
                Clear
            </UButton>
        </template>
        <UIDatePicker
            v-if="['datetime', 'time', 'date'].includes(field.type)"
            :mode="
                {
                    datetime: 'dateTime',
                }[field.type] ?? field.type
            "
            :model-value="field.default_value"
            :name="name"
            @update:modelValue="field = { ...field, default_value: $event }"
        />
        <HdColorPicker
            v-else-if="field.type === 'string' && field.sub_type === 'color'"
            :name="name"
            :pure-color="field.default_value"
            @update:pureColor="field = { ...field, default_value: $event }"
        />
        <HdInput
            v-else-if="field.type === 'string' && field.sub_type === 'phone'"
            :fields="phones"
            :model-value="field.default_value"
            :name="name"
            placeholder="Select email"
            @update:modelValue="field = { ...field, default_value: $event }"
        />
        <HdInput
            v-else-if="field.type === 'string' && field.sub_type === 'email'"
            :fields="emails"
            :model-value="field.default_value"
            :name="name"
            placeholder="Select email"
            @update:modelValue="field = { ...field, default_value: $event }"
        />

        <MediaLibrary
            v-else-if="field.type === 'string' && field.sub_type === 'image'"
        >
            <template #default="{ toggle }">
                <UButton
                    block
                    class="justify-start"
                    color="white"
                    size="lg"
                    @click="toggle"
                >
                    <template v-if="!field.default_value" #leading>
                        <FontAwesomeIcon :icon="faImage" size="xl" />
                    </template>
                    <span v-if="!field.default_value">Choose image</span>
                    <nuxt-img v-else :src="field.default_value" class="h-24" />
                </UButton>
            </template>
            <template #action="{ toggle, selected }">
                <UButton
                    block
                    color="primary"
                    size="lg"
                    @click="chooseImage(selected, toggle)"
                >
                    Add media to field
                </UButton>
            </template>
        </MediaLibrary>
        <HdInput
            v-else-if="field.type === 'string'"
            :fields="fields"
            :mode="field.sub_type === 'wysiwyg' ? 'html' : 'text'"
            :model-value="field.default_value"
            :name="name"
            :textarea="field.sub_type === 'wysiwyg'"
            :with-menu="field.sub_type === 'wysiwyg'"
            @update:modelValue="field = { ...field, default_value: $event }"
        />
    </UFormGroup>
</template>
